import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  ChartBarIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { Button, Card, Badge, Modal, Input, Select } from '@realvisor/ui';

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: 'transactional' | 'sequence' | 'manual' | 'system';
  subject: string;
  isActive: boolean;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  transactional: 'Transakční',
  sequence: 'Sekvence',
  manual: 'Ruční',
  system: 'Systémový',
};

const categoryVariants: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  transactional: 'info',
  sequence: 'primary',
  manual: 'default',
  system: 'warning',
};

export function EmailTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentProject, isAllProjects, projects, switchProject } = useProject();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'transactional',
    subject: '',
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-templates', currentProject?.id],
    queryFn: () => api.get<EmailTemplate[]>(`/email-templates${currentProject ? `?projectId=${currentProject.id}` : ''}`),
    enabled: !!currentProject,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post('/email-templates', { ...data, projectId: currentProject?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates', currentProject?.id] });
      setIsCreateModalOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      api.put(`/email-templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setEditingTemplate(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/email-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/email-templates/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => api.post(`/email-templates/actions/seed/${currentProject?.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      category: 'transactional',
      subject: '',
    });
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      code: template.code,
      name: template.name,
      description: template.description || '',
      category: template.category,
      subject: template.subject,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getOpenRate = (template: EmailTemplate) => {
    if (template.sentCount === 0) return 0;
    return Math.round((template.openedCount / template.sentCount) * 100);
  };

  const getClickRate = (template: EmailTemplate) => {
    if (template.openedCount === 0) return 0;
    return Math.round((template.clickedCount / template.openedCount) * 100);
  };

  // Zobrazit výzvu k výběru projektu pokud je vybráno "Všechny projekty"
  if (isAllProjects) {
    return (
      <div className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            <NavLink
              to="/email/templates"
              className={({ isActive }) =>
                `pb-3 text-sm font-medium border-b-2 ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
            >
              Šablony
            </NavLink>
            <NavLink
              to="/email/sequences"
              className={({ isActive }) =>
                `pb-3 text-sm font-medium border-b-2 ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
            >
              Sekvence
            </NavLink>
          </nav>
        </div>

        <Card className="p-12">
          <div className="text-center">
            <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Vyberte projekt</h3>
            <p className="mt-2 text-sm text-gray-500">
              Pro správu emailových šablon vyberte konkrétní projekt v sidebaru.
            </p>
            {projects.length > 0 && (
              <div className="mt-6">
                <select
                  value=""
                  onChange={(e) => switchProject(e.target.value)}
                  className="block w-full max-w-xs mx-auto rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="" disabled>Vyberte projekt...</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <NavLink
            to="/email/templates"
            className={({ isActive }) =>
              `pb-3 text-sm font-medium border-b-2 ${
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`
            }
          >
            Šablony
          </NavLink>
          <NavLink
            to="/email/sequences"
            className={({ isActive }) =>
              `pb-3 text-sm font-medium border-b-2 ${
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`
            }
          >
            Sekvence
          </NavLink>
        </nav>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email šablony</h1>
          <p className="text-gray-500 mt-1">Správa emailových šablon pro automatické odesílání</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
          >
            {seedMutation.isPending ? 'Vytvářím...' : 'Vytvořit výchozí šablony'}
          </Button>
          <Button onClick={() => navigate('/email/templates/new')}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Nová šablona
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <EnvelopeIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Celkem šablon</p>
              <p className="text-xl font-semibold">{templates?.length || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Odesláno</p>
              <p className="text-xl font-semibold">
                {templates?.reduce((sum: number, t: EmailTemplate) => sum + t.sentCount, 0) || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Průměr. open rate</p>
              <p className="text-xl font-semibold">
                {templates && templates.length > 0
                  ? Math.round(
                      templates.reduce((sum: number, t: EmailTemplate) => sum + getOpenRate(t), 0) / templates.length
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Průměr. click rate</p>
              <p className="text-xl font-semibold">
                {templates && templates.length > 0
                  ? Math.round(
                      templates.reduce((sum: number, t: EmailTemplate) => sum + getClickRate(t), 0) / templates.length
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates?.map((template: EmailTemplate) => (
          <Card key={template.id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-900">{template.name}</h3>
                  {!template.isActive && (
                    <Badge variant="default">Neaktivní</Badge>
                  )}
                </div>
                <Badge variant={categoryVariants[template.category]}>
                  {categoryLabels[template.category]}
                </Badge>
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-2 line-clamp-1">
              {template.subject}
            </p>

            {template.description && (
              <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                {template.description}
              </p>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span>{template.sentCount} odesláno</span>
              <span>{getOpenRate(template)}% open</span>
              <span>{getClickRate(template)}% click</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/email/templates/${template.id}`)}
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Upravit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => duplicateMutation.mutate(template.id)}
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (confirm('Opravdu chcete smazat tuto šablonu?')) {
                    deleteMutation.mutate(template.id);
                  }
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}

        {templates?.length === 0 && (
          <Card className="col-span-full p-8 text-center">
            <EnvelopeIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Žádné šablony
            </h3>
            <p className="text-gray-500 mb-4">
              Vytvořte první emailovou šablonu pro automatické odesílání.
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Vytvořit šablonu
            </Button>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || !!editingTemplate}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingTemplate(null);
          resetForm();
        }}
        title={editingTemplate ? 'Upravit šablonu' : 'Nová šablona'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Kód"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="valuation-result"
              required
              disabled={!!editingTemplate}
            />
            <Input
              label="Název"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Výsledek odhadu"
              required
            />
          </div>

          <Select
            label="Kategorie"
            value={formData.category}
            onChange={(value) => setFormData({ ...formData, category: value })}
            options={[
              { value: 'transactional', label: 'Transakční' },
              { value: 'sequence', label: 'Sekvence' },
              { value: 'manual', label: 'Ruční' },
              { value: 'system', label: 'Systémový' },
            ]}
          />

          <Input
            label="Předmět"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Váš odhad nemovitosti je připraven"
            required
          />

          <Input
            label="Popis"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Interní popis šablony..."
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingTemplate(null);
                resetForm();
              }}
            >
              Zrušit
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingTemplate ? 'Uložit' : 'Vytvořit'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
